import React, { useState } from 'react';
import profile from '../../../assets/images/profile.png';
import { VideoPlayerModal } from '../../VideoPlayer/VideoPlayerModal';
import { normalizePlayerClass } from '../../../utils/normalizePlayerClass';

export const UploadedVideos = ({ followedPlayers = [] }) => {
  // Filter players who have videos
  const playersWithVideos = followedPlayers.filter(player => player.videos && player.videos.length > 0);

  // State for video player modal
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedPlayerProfile, setSelectedPlayerProfile] = useState(null);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);

  const handleVideoClick = (video, playerProfile, allVideos) => {
    setSelectedVideo(video);
    setSelectedPlayerProfile({ ...playerProfile, videos: allVideos });
    setIsPlayerOpen(true);
  };

  const handleClosePlayer = () => {
    setIsPlayerOpen(false);
    setSelectedVideo(null);
    setSelectedPlayerProfile(null);
  };
  return (
    <div className="uploaded-videos-container bg-[#181e29] rounded-2xl p-3 sm:p-4 md:p-6 lg:p-8">
      {/* Vertical scroll for players list */}
      <div className="players-list flex flex-col gap-5 sm:gap-6 md:gap-8 lg:gap-10">
        {playersWithVideos.length > 0 ? playersWithVideos.map((player, index) => {
          const playerName = player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'N/A';
          const position = player.primaryPosition || player.position || 'N/A';
          const playerClass = normalizePlayerClass(player.playerClass || player.class) || 'N/A';
          const playerAvatar = player.profileImage || profile;

          return (
            <div
              key={player._id || index}
              className={`player-row flex flex-row items-start gap-3 sm:gap-4 md:gap-6 lg:gap-8 w-full pb-5 sm:pb-6 md:pb-8 ${
                index !== playersWithVideos.length - 1 ? 'border-b border-jp-line' : ''
              }`}
            >
              {/* Player Info - Left Side */}
              <div className="player-info flex flex-col items-center flex-shrink-0 w-[70px] sm:w-[90px] md:w-[110px] lg:w-[130px]">
                <img
                  src={playerAvatar}
                  alt={playerName}
                  className="rounded-lg w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 object-cover border-2 border-jp-line mb-2"
                  onError={(e) => {
                    e.target.src = profile;
                  }}
                />
                <div className="text-jp-ink font-semibold text-[10px] sm:text-xs md:text-sm text-center leading-tight">
                  {playerName}
                </div>
                <div className="text-jp-muted text-[9px] sm:text-[10px] md:text-xs text-center mt-0.5">
                  {position}
                </div>
                <div className="text-jp-accent-ink text-[9px] sm:text-[10px] md:text-xs font-medium">
                  {playerClass}
                </div>
              </div>

              {/* Videos Container - Right Side with Horizontal Scroll */}
              <div className="videos-wrapper flex-1 min-w-0">
                <div className="videos-scroll-container overflow-x-auto pb-3">
                  <div className="videos-row flex flex-row gap-2 sm:gap-3 md:gap-4 lg:gap-5" style={{ width: 'max-content' }}>
                    {player.videos && player.videos.map((video, i) => (
                      <div
                        key={video._id || i}
                        onClick={() => handleVideoClick(video, player, player.videos)}
                        className="video-card relative rounded-lg sm:rounded-xl bg-jp-section shadow-lg overflow-hidden flex-shrink-0 cursor-pointer hover:scale-105 transition-transform duration-200
                          w-[60px] h-[85px]
                          sm:w-[80px] sm:h-[110px]
                          md:w-[100px] md:h-[140px]
                          lg:w-[120px] lg:h-[165px]"
                      >
                        <video
                          src={video.url}
                          className="w-full h-full object-cover pointer-events-none"
                          preload="metadata"
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center hover:bg-black/20 transition-colors">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <svg
                              className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 ml-0.5"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <polygon points="8,6 19,12 8,18"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        }) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <svg className="w-16 h-16 text-jp-muted/70 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <p className="text-jp-muted text-lg font-medium mb-2">No videos uploaded yet</p>
            <p className="text-jp-muted text-sm max-w-md">
              Your followed players haven't uploaded any videos yet. Check back later to see their highlights.
            </p>
          </div>
        )}
      </div>

      {/* Scroll Hint - Mobile Only */}
      <div className="md:hidden text-center pt-3 text-[10px] sm:text-xs text-jp-muted">
        ← Swipe videos horizontally • Scroll down for more players ↓
      </div>

      <style>{`
        .uploaded-videos-container {
          max-height: 70vh;
          min-height: 280px;
        }

        .players-list {
          max-height: calc(70vh - 60px);
          overflow-y: auto;
          padding-right: 8px;
        }

        /* Custom Scrollbar for Vertical (Players List) */
        .players-list::-webkit-scrollbar {
          width: 8px;
        }
        .players-list::-webkit-scrollbar-track {
          background: rgb(var(--jp-section));
          border-radius: 4px;
          margin: 4px 0;
        }
        .players-list::-webkit-scrollbar-thumb {
          background: rgb(var(--jp-line));
          border-radius: 4px;
          border: 1px solid rgb(var(--jp-section));
        }
        .players-list::-webkit-scrollbar-thumb:hover {
          background: rgb(var(--jp-muted));
        }

        /* Custom Scrollbar for Horizontal (Videos) */
        .videos-scroll-container::-webkit-scrollbar {
          height: 6px;
        }
        .videos-scroll-container::-webkit-scrollbar-track {
          background: rgb(var(--jp-section));
          border-radius: 3px;
          margin: 0 4px;
        }
        .videos-scroll-container::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, #06b6d4 0%, #0891b2 100%);
          border-radius: 3px;
        }
        .videos-scroll-container::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(90deg, #22d3ee 0%, #06b6d4 100%);
        }

        /* Firefox Scrollbar Support */
        .players-list {
          scrollbar-width: thin;
          scrollbar-color: rgb(var(--jp-line)) rgb(var(--jp-section));
        }
        .videos-scroll-container {
          scrollbar-width: thin;
          scrollbar-color: rgb(var(--jp-accent)) rgb(var(--jp-section));
        }

        /* Responsive adjustments */
        @media (max-width: 640px) {
          .uploaded-videos-container {
            max-height: 60vh;
          }
          .players-list {
            max-height: calc(60vh - 50px);
            padding-right: 4px;
          }
          .players-list::-webkit-scrollbar {
            width: 4px;
          }
          .videos-scroll-container::-webkit-scrollbar {
            height: 4px;
          }
        }

        @media (min-width: 1024px) {
          .uploaded-videos-container {
            max-height: 75vh;
          }
          .players-list {
            max-height: calc(75vh - 80px);
          }
        }
      `}</style>

      {/* Video Player Modal */}
      <VideoPlayerModal
        isOpen={isPlayerOpen}
        onClose={handleClosePlayer}
        video={selectedVideo}
        profile={selectedPlayerProfile}
        allVideos={selectedPlayerProfile?.videos || []}
      />
    </div>
  );
};
